// Staff open the ops console from their customer app session (migration 0203, owner decision
// 2026-11-01). A signed-out ops page keeps a random verifier in this tab and sends the person to
// the customer app with its S256 challenge; the customer app sends a one-time handoff back in
// the URL fragment, and this page redeems it with the verifier. A handoff that reached another
// tab or browser is useless without the verifier.

import { auth } from "../api/ops";
import type { OpsMe } from "../api/types";

/** `sessionStorage` on the ops host: the pending handoff of this tab. */
export const HANDOFF_KEY = "kite.ops.handoff";

interface Pending {
  verifier: string;
  state: string;
  to: string | null;
}

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function random(n: number): string {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b64url(b);
}

/** The S256 challenge of a verifier. */
export async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

/** How the page leaves for the customer app. Tests replace it. */
export const leave = {
  to: (href: string) => window.location.assign(href),
};

/**
 * Starts a handoff: the customer app's `/platform-handoff` with this tab's challenge. `to` is the
 * ops page to open after. The customer app's origin comes from the ops server, never from the
 * address.
 */
export async function startHandoff(
  to: string | null,
  { fresh = false }: { fresh?: boolean } = {},
): Promise<void> {
  const { app_url } = await auth.handoffOrigin();
  const pending: Pending = { verifier: random(32), state: random(16), to };
  sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(pending));
  const q = new URLSearchParams({ challenge: await challengeOf(pending.verifier), state: pending.state });
  // "Configure now" needs a fresh sign-in on the customer app first (migration 0204).
  if (fresh) q.set("fresh", "1");
  leave.to(`${app_url.replace(/\/+$/, "")}/platform-handoff?${q.toString()}`);
}

/** The handoff in a `#handoff=...&state=...` fragment, or null. */
export function handoffInFragment(hash: string): { token: string; state: string } | null {
  const q = new URLSearchParams(hash.replace(/^#/, ""));
  const token = q.get("handoff");
  const state = q.get("state");
  return token && state ? { token, state } : null;
}

/**
 * Redeems the handoff with this tab's verifier. Throws when this tab did not ask for it (another
 * tab, another browser, a link someone sent). Returns the staff member and the page to open.
 */
export async function redeemHandoff(h: {
  token: string;
  state: string;
}): Promise<{ me: OpsMe; to: string | null }> {
  let pending: Pending | null = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(HANDOFF_KEY) ?? "null") as Pending | null;
  } catch {
    pending = null;
  }
  sessionStorage.removeItem(HANDOFF_KEY);
  if (!pending || pending.state !== h.state) throw new Error("That sign-in link expired. Try again.");
  const me = await auth.redeemHandoff(h.token, pending.verifier);
  return { me, to: pending.to };
}
